import i18n from "i18next";
import _ from "lodash";
import dayjs from "dayjs";
import { ORDER_STATUS_CANCELED, ORDER_STATUS_CANCELLED } from "../constants/OrderConstant";
import advancedFormat from "dayjs/plugin/advancedFormat";
import isoWeek from "dayjs/plugin/isoWeek";
import { CHEFS_FARM, GROWY } from "../constants/CompanyConstants";
import { FARM_PRODUCT_GROUPS, RETURNABLE_PACKAGING_PRODUCT_GROUP } from "../constants/ProductConstant";

dayjs.extend(isoWeek);
dayjs.extend(advancedFormat);
const DATE_FORMAT = "DD-MM-YYYY";

/**
 * Expand a cart item into its components if it's a bundle
 * First checks if bundle data exists in the cart item itself (new orders)
 * Falls back to products collection for backwards compatibility (old orders)
 */
const expandBundleItem = (cartItem, allProducts) => {
  const productDetails = allProducts.find((p) => p.id === cartItem.productId);

  // Check if this is a bundle (either from cart item or product details)
  const isBundle = cartItem.isBundle || productDetails?.isBundle;

  if (!isBundle) {
    const weight = productDetails?.weight || 0;
    return [
      {
        ...cartItem,
        quantity: cartItem.itemCount,
        weight: weight,
        isFromBundle: false,
      },
    ];
  }

  const expandedItems = [];

  // NEW: Check if bundle data exists in the cart item itself (new orders)
  const bundleItemsSource =
    cartItem.bundleItems && Array.isArray(cartItem.bundleItems) && cartItem.bundleItems.length > 0
      ? cartItem.bundleItems
      : productDetails?.bundleItems; // Fallback to products collection (old orders)

  const packagingMaterialSource = cartItem.packagingMaterial
    ? cartItem.packagingMaterial
    : productDetails?.packagingMaterial; // Fallback to products collection (old orders)

  // Add bundle items
  if (bundleItemsSource && Array.isArray(bundleItemsSource)) {
    bundleItemsSource.forEach((bundleItem) => {
      const itemDetails = allProducts.find((p) => p.id === bundleItem.productId);
      expandedItems.push({
        productId: bundleItem.productId,
        productName: itemDetails?.productName || itemDetails?.name?.en || bundleItem.productId,
        productCategory: itemDetails?.productCategory || itemDetails?.category?.en || "",
        productImageDownloadURL: itemDetails?.productImageDownloadURL || "",
        itemCount: bundleItem.quantity * cartItem.itemCount,
        // bundleItem may be a cart-line snapshot (productPrice) or a product-master bundle item (price)
        productPrice: bundleItem.productPrice ?? bundleItem.price,
        weight: itemDetails?.weight || 0,
        isFromBundle: true,
        originalBundleId: cartItem.productId,
      });
    });
  }

  // Add packaging material
  if (packagingMaterialSource) {
    const packagingDetails = allProducts.find((p) => p.id === packagingMaterialSource.productId);
    expandedItems.push({
      productId: packagingMaterialSource.productId,
      productName: packagingDetails?.productName || packagingDetails?.name?.en || "",
      productCategory: packagingDetails?.productCategory || packagingDetails?.category?.en || "",
      productImageDownloadURL: packagingDetails?.productImageDownloadURL || "",
      itemCount: packagingMaterialSource.quantity * cartItem.itemCount,
      // packagingMaterialSource may be a cart-line snapshot (productPrice) or product-master (price)
      productPrice: packagingMaterialSource.productPrice ?? packagingMaterialSource.price,
      weight: packagingDetails?.weight || 0,
      isFromBundle: true,
      originalBundleId: cartItem.productId,
    });
  }

  return expandedItems;
};

/**
 * Expand shopping cart to include bundle components
 * Filters out packaging compensation lines to avoid double counting in credit invoices
 */
export const expandCartBundles = (userShoppingCart, allProducts) => {
  if (!userShoppingCart || !Array.isArray(userShoppingCart)) {
    return [];
  }

  // Find all positive packaging compensation lines
  const packagingCompensationLines = userShoppingCart
    .filter((item) => item.isPackagingLine && item.itemCount > 0)
    .map((item) => ({
      packagingId: item.productId,
      quantity: item.itemCount,
    }));

  const expandedCart = [];

  userShoppingCart.forEach((cartItem) => {
    // Skip positive packaging lines that are compensation for credited bundles
    if (cartItem.isPackagingLine && cartItem.itemCount > 0) {
      // Check if this is a compensation line (will be matched with a credited bundle)
      const isCreditedBundleInCart = userShoppingCart.some(
        (item) =>
          item.isBundle &&
          item.isCreditLine &&
          item.itemCount < 0 &&
          item.packagingMaterial &&
          item.packagingMaterial.productId === cartItem.productId &&
          Math.abs(item.itemCount) * item.packagingMaterial.quantity === cartItem.itemCount,
      );

      if (isCreditedBundleInCart) {
        // Skip this compensation line
        return;
      }
    }

    // For credited bundles, check if packaging should be excluded
    if (cartItem.isBundle && cartItem.isCreditLine && cartItem.itemCount < 0 && cartItem.packagingMaterial) {
      const expectedPackagingQty = Math.abs(cartItem.itemCount) * cartItem.packagingMaterial.quantity;
      const hasCompensationLine = packagingCompensationLines.some(
        (comp) => comp.packagingId === cartItem.packagingMaterial.productId && comp.quantity === expectedPackagingQty,
      );

      if (hasCompensationLine) {
        // Expand bundle WITHOUT packaging material
        const expandedItems = expandBundleItemWithoutPackaging(cartItem, allProducts);
        expandedCart.push(...expandedItems);
        return;
      }
    }

    const expandedItems = expandBundleItem(cartItem, allProducts);
    expandedCart.push(...expandedItems);
  });

  return expandedCart;
};

/**
 * Expand bundle item without packaging material (used for credited bundles with compensation)
 */
const expandBundleItemWithoutPackaging = (cartItem, allProducts) => {
  const productDetails = allProducts.find((p) => p.id === cartItem.productId);
  const isBundle = cartItem.isBundle || productDetails?.isBundle;

  if (!isBundle) {
    const weight = productDetails?.weight || 0;
    return [
      {
        ...cartItem,
        quantity: cartItem.itemCount,
        weight: weight,
        isFromBundle: false,
      },
    ];
  }

  const expandedItems = [];

  const bundleItemsSource =
    cartItem.bundleItems && Array.isArray(cartItem.bundleItems) && cartItem.bundleItems.length > 0
      ? cartItem.bundleItems
      : productDetails?.bundleItems;

  // Add bundle items only (skip packaging)
  if (bundleItemsSource && Array.isArray(bundleItemsSource)) {
    bundleItemsSource.forEach((bundleItem) => {
      const itemDetails = allProducts.find((p) => p.id === bundleItem.productId);
      expandedItems.push({
        productId: bundleItem.productId,
        productName: itemDetails?.productName || itemDetails?.name?.en || bundleItem.productId,
        productCategory: itemDetails?.productCategory || itemDetails?.category?.en || "",
        productImageDownloadURL: itemDetails?.productImageDownloadURL || "",
        itemCount: bundleItem.quantity * cartItem.itemCount,
        // bundleItem may be a cart-line snapshot (productPrice) or a product-master bundle item (price)
        productPrice: bundleItem.productPrice ?? bundleItem.price,
        weight: itemDetails?.weight || 0,
        isFromBundle: true,
        originalBundleId: cartItem.productId,
      });
    });
  }

  return expandedItems;
};

/**
 * Expand credited products to include bundle components
 * First checks if bundle data exists in the credited product itself (new credits)
 * Falls back to products collection for backwards compatibility (old credits)
 */
const expandCreditedBundles = (creditedProducts, allProducts) => {
  if (!creditedProducts || !Array.isArray(creditedProducts)) {
    return [];
  }

  const expandedCredits = [];

  creditedProducts.forEach((creditedProduct) => {
    const productDetails = allProducts.find((p) => p.id === creditedProduct.productId);

    // Check if this is a bundle (either from credited product or product details)
    const isBundle = creditedProduct.isBundle || productDetails?.isBundle;

    if (!isBundle) {
      expandedCredits.push(creditedProduct);
      return;
    }

    // NEW: Check if bundle data exists in the credited product itself (new credits)
    const bundleItemsSource =
      creditedProduct.bundleItems &&
      Array.isArray(creditedProduct.bundleItems) &&
      creditedProduct.bundleItems.length > 0
        ? creditedProduct.bundleItems
        : productDetails?.bundleItems; // Fallback to products collection (old credits)

    const packagingMaterialSource = creditedProduct.packagingMaterial
      ? creditedProduct.packagingMaterial
      : productDetails?.packagingMaterial; // Fallback to products collection (old credits)

    if (bundleItemsSource) {
      bundleItemsSource.forEach((bundleItem) => {
        expandedCredits.push({
          productId: bundleItem.productId,
          quantity: bundleItem.quantity * creditedProduct.quantity,
        });
      });
    }

    if (packagingMaterialSource) {
      expandedCredits.push({
        productId: packagingMaterialSource.productId,
        quantity: packagingMaterialSource.quantity * creditedProduct.quantity,
      });
    }
  });

  return expandedCredits;
};

/**
 * Calculate total credits for an order based on related invoices
 * @param {Object} order - The order object
 * @param {Array} allInvoices - All invoices in the system
 * @param {Array} allOrders - All orders (needed for proportional credit calculation)
 * @param {Array} allProducts - All products (needed for bundle expansion)
 * @returns {Object} { totalCredit: number, creditedProducts: [{productId, quantity}] }
 */
export const calculateOrderCredits = (order, allInvoices, allOrders, allProducts = []) => {
  let totalCredit = 0;
  const creditedProductsMap = new Map(); // productId -> total credited quantity

  // Find all invoices that contain this order
  const relatedInvoices = allInvoices.filter(
    (invoice) => invoice.farmorderIds && invoice.farmorderIds.includes(order.farmorderId || order.id),
  );

  relatedInvoices.forEach((invoice) => {
    let creditAmount = 0;
    let creditedProducts = [];

    // Case 1: Invoice has direct credits (draft/in_exact with creditedAmount)
    if (invoice.creditedAmount && invoice.creditedAmount !== 0) {
      creditAmount = Math.abs(invoice.creditedAmount); // Use absolute value
      creditedProducts = invoice.creditedProducts || [];
    }

    // Case 2: Invoice has a separate credit invoice (processed orders)
    if (invoice.creditInvoiceId) {
      const creditInvoice = allInvoices.find((inv) => inv.id === invoice.creditInvoiceId);
      if (creditInvoice) {
        creditAmount = Math.abs(creditInvoice.totalAmount || 0);
        // Extract credited products from credit invoice shopping cart
        creditedProducts = (creditInvoice.userShoppingCart || [])
          .filter((item) => item.itemCount < 0)
          .map((item) => ({
            productId: item.productId,
            creditedQuantity: Math.abs(item.itemCount),
          }));
      }
    }

    // Expand credited bundles
    if (allProducts.length > 0) {
      creditedProducts = expandCreditedBundles(
        creditedProducts.map((cp) => ({ productId: cp.productId, quantity: cp.creditedQuantity })),
        allProducts,
      );
    }

    // If this invoice contains multiple orders, calculate proportional credit
    if (creditAmount > 0 && invoice.farmorderIds.length > 1) {
      // Find all orders in this invoice
      const ordersInInvoice = invoice.farmorderIds
        .map((orderId) => allOrders.find((o) => (o.farmorderId || o.id) === orderId))
        .filter(Boolean);

      // Calculate total amount of all orders in invoice
      const totalInvoiceAmount = ordersInInvoice.reduce((sum, o) => sum + (o.totalAmount || 0), 0);

      // Calculate this order's share
      const orderShare = totalInvoiceAmount > 0 ? (order.totalAmount || 0) / totalInvoiceAmount : 0;

      // Apply proportional credit
      creditAmount = creditAmount * orderShare;

      // Also apply proportion to credited product quantities
      creditedProducts = creditedProducts.map((cp) => ({
        ...cp,
        quantity: Math.round((cp.quantity || cp.creditedQuantity) * orderShare),
      }));
    }

    // Add to totals
    totalCredit += creditAmount;

    // Aggregate credited products
    creditedProducts.forEach((cp) => {
      const existing = creditedProductsMap.get(cp.productId) || 0;
      creditedProductsMap.set(cp.productId, existing + (cp.quantity || cp.creditedQuantity || 0));
    });
  });

  return {
    totalCredit: Math.round(totalCredit * 100) / 100, // Round to 2 decimals
    creditedProducts: Array.from(creditedProductsMap.entries()).map(([productId, quantity]) => ({
      productId,
      quantity,
    })),
  };
};

/**
 * Calculate the ordered quantity for a specific product in an order
 * Takes into account only customer-initiated changes, not company changes
 * Handles bundle expansion
 * @param {Object} order - The order object
 * @param {string} productId - The product ID to calculate for
 * @param {Array} allProducts - All products (needed for bundle expansion)
 * @returns {number} The ordered quantity
 */
export const calculateOrderedQuantity = (order, productId, allProducts = []) => {
  // Helper to get quantity for a product from a cart (with bundle expansion)
  const getQuantityFromCart = (cart) => {
    if (!cart) return 0;

    const expandedCart = expandCartBundles(cart, allProducts);
    const item = expandedCart.find((item) => item.productId === productId);
    return item?.itemCount || 0;
  };

  // Start with the original order (first previousShoppingCart or userShoppingCart if no history)
  const originalCart =
    order.previousShoppingCarts?.length > 0 ? order.previousShoppingCarts[0].items : order.userShoppingCart;

  let orderedQuantity = getQuantityFromCart(originalCart);

  // If there are previous carts, track customer changes
  if (order.previousShoppingCarts?.length > 0) {
    // Loop through all versions to track customer changes
    for (let i = 0; i < order.previousShoppingCarts.length; i++) {
      const currentCart = order.previousShoppingCarts[i].items || [];

      // Check if any item in this cart version was changed by customer
      const hasCustomerChange = currentCart.some((item) => item.changeRequestedBy === "customer");

      if (hasCustomerChange) {
        // Get previous version to calculate delta
        const prevCart = i === 0 ? originalCart : order.previousShoppingCarts[i - 1].items;

        const prevQty = getQuantityFromCart(prevCart);
        const currentQty = getQuantityFromCart(currentCart);
        const delta = currentQty - prevQty;

        orderedQuantity += delta;
      }
    }

    // Check final cart for customer changes
    const finalCart = order.userShoppingCart || [];
    const hasCustomerChange = finalCart.some((item) => item.changeRequestedBy === "customer");

    if (hasCustomerChange) {
      const lastPrevCart = order.previousShoppingCarts[order.previousShoppingCarts.length - 1].items;

      const prevQty = getQuantityFromCart(lastPrevCart);
      const finalQty = getQuantityFromCart(finalCart);
      const delta = finalQty - prevQty;

      orderedQuantity += delta;
    }
  }

  return orderedQuantity;
};

export const getSalesDashboardData = (
  orders,
  start,
  end,
  companyFilter = "All",
  allProducts = [],
  allInvoices = [],
  customers = [],
  marketingCodeFilter = "All",
) => {
  if (!!orders) {
    // Filter out cancelled orders first
    let filteredOrders = orders.filter(
      (order) => order.farmorderStatus !== ORDER_STATUS_CANCELED && order.farmorderStatus !== ORDER_STATUS_CANCELLED,
    );

    // Apply company filter based on customer.company
    if (companyFilter !== "All") {
      filteredOrders = filteredOrders.filter((order) => {
        // Find the customer for this order
        const customer = customers.find((c) => c.id === order.farmorderCustomerId);
        const customerCompany = customer?.company;

        if (companyFilter === GROWY) {
          // Growy orders have customer.company === "GR"
          return customerCompany === GROWY;
        } else if (companyFilter === CHEFS_FARM) {
          // Chef's Farm orders have customer.company === "CF" or null/undefined
          return customerCompany === CHEFS_FARM || !customerCompany;
        }
        return false;
      });
    }

    // Apply marketing code filter based on customer.marketingCode
    if (marketingCodeFilter !== "All") {
      filteredOrders = filteredOrders.filter((order) => {
        const customer = customers.find((c) => c.id === order.farmorderCustomerId);
        return customer?.marketingCode === marketingCodeFilter;
      });
    }

    const today = dayjs();
    let minDate = dayjs(start, "x").format(DATE_FORMAT);
    let maxDate = dayjs(end, "x").format(DATE_FORMAT);
    if (!start || !end) {
      minDate = dayjs(today).startOf("isoWeek").format(DATE_FORMAT);
      maxDate = dayjs(today).endOf("isoWeek").format(DATE_FORMAT);
    }

    const minDatePrevPeriod = dayjs(minDate, DATE_FORMAT).subtract(7, "days").format(DATE_FORMAT);
    const maxDatePrevPeriod = dayjs(today).subtract(8, "days").format(DATE_FORMAT);
    const dates = getRange(minDate, maxDate, "days");
    const datesPrevPeriod = getRange(minDatePrevPeriod, maxDatePrevPeriod, "days");
    const formattedDates = _.map(dates, (date) => dayjs(date, "DD-MM-YYYY").format("D MMM 'YY"));

    // Initialize arrays for each revenue category
    let farmRevenues = [];
    let farmCredits = [];
    let shippingRevenues = [];
    let returnablePackagingRevenues = [];
    let additionalSalesRevenues = [];

    let totalRevenue = 0;
    let totalFarmRevenue = 0;
    let totalFarmCredits = 0;
    let totalShippingCost = 0;
    let totalReturnablePackaging = 0;
    let totalAdditionalSales = 0;
    let totalDiscounts = 0;
    let totalRevenueToday = 0;
    let totalRevenuePrevPeriod = 0;
    let totalOrders = 0;
    let totalOrdersPrevPeriod = 0;
    let ordersInPeriod = [];
    let totalWeight = 0;
    let totalUnitsSold = 0;
    let totalRevenueWithWeight = 0;
    const readableDateRange =
      dayjs(dates[0], DATE_FORMAT).format("D MMM") +
      " - " +
      dayjs(dates[dates.length - 1], DATE_FORMAT).format("D MMM YYYY");
    const boughtProducts = [];
    const customerRevenues = [];

    // Process standalone credit invoices (not linked to orders)
    const standaloneCredits = allInvoices.filter(
      (invoice) =>
        invoice.invoiceType === "credit" &&
        (!invoice.farmorderIds || invoice.farmorderIds.length === 0) &&
        invoice.totalAmount &&
        invoice.totalAmount < 0,
    );

    _.map(standaloneCredits, (creditInvoice) => {
      const creditDate = dayjs(creditInvoice.invoiceDate).format(DATE_FORMAT);

      // Only process credits in the current period
      if (dates.includes(creditDate)) {
        const index = _.findIndex(dates, (item) => item === creditDate);
        const creditAmount = Math.abs(creditInvoice.totalAmount);

        // Initialize revenue arrays for this date if needed
        if (!farmRevenues[index]) farmRevenues[index] = 0;
        if (!farmCredits[index]) farmCredits[index] = 0;
        if (!shippingRevenues[index]) shippingRevenues[index] = 0;
        if (!returnablePackagingRevenues[index]) returnablePackagingRevenues[index] = 0;
        if (!additionalSalesRevenues[index]) additionalSalesRevenues[index] = 0;

        // Expand shopping cart to handle bundles
        const expandedCart = expandCartBundles(creditInvoice.userShoppingCart || [], allProducts);

        // Process each credited product
        _.map(expandedCart, (cartProduct) => {
          // Credit invoices have negative itemCount
          const quantity = Math.abs(cartProduct.itemCount);
          const paymentPrice =
            typeof cartProduct.productPrice === "string"
              ? parseFloat(cartProduct.productPrice)
              : cartProduct.productPrice;
          const creditRevenue = paymentPrice * quantity;

          // Get product details to find productGroup
          const productDetails = allProducts.find((p) => p.id === cartProduct.productId);
          const productGroup = productDetails?.productGroup || "other";
          const productWeight = productDetails?.weight || 0;

          // Subtract from total units sold
          totalUnitsSold -= quantity;

          // Track weight for products with weight > 0
          if (productWeight > 0) {
            totalWeight -= productWeight * quantity;
            totalRevenueWithWeight -= creditRevenue;
          }

          // Categorize credit based on productGroup
          if (FARM_PRODUCT_GROUPS.includes(productGroup)) {
            farmCredits[index] += creditRevenue;
            totalFarmCredits += creditRevenue;
          } else if (productGroup === RETURNABLE_PACKAGING_PRODUCT_GROUP) {
            returnablePackagingRevenues[index] -= creditRevenue;
            totalReturnablePackaging -= creditRevenue;
          } else {
            // packaging, other, or any unknown group
            additionalSalesRevenues[index] -= creditRevenue;
            totalAdditionalSales -= creditRevenue;
          }

          // Track product sales (subtract credits)
          const productIndex = _.findIndex(boughtProducts, (item) => item.id === cartProduct.productId);
          if (productIndex !== -1) {
            boughtProducts[productIndex].timesCredited = (boughtProducts[productIndex].timesCredited || 0) + quantity;
            boughtProducts[productIndex].totalRevenue -= creditRevenue;
          } else {
            // Create entry for credited product even if not sold in period
            boughtProducts.push({
              id: cartProduct.productId,
              timesBought: 0,
              timesCredited: quantity,
              totalRevenue: -creditRevenue,
              img: cartProduct.productImageDownloadURL,
              name: cartProduct.productName,
              category: cartProduct.productCategory,
            });
          }
        });
      }
    });

    // Loop over all filtered orders to calculate the desired data
    _.map(filteredOrders, (order) => {
      if (!!order.totalAmount) {
        const deliveryDate = dayjs(order.farmorderDeliveryDate).format(DATE_FORMAT);
        // Calculations for current period
        if (dates.includes(deliveryDate)) {
          ordersInPeriod.push(order);
          const index = _.findIndex(dates, (item) => item === deliveryDate);

          // Calculate credits for this order (with bundle expansion)
          const orderCredits = calculateOrderCredits(order, allInvoices, filteredOrders, allProducts);
          const orderCreditAmount = orderCredits.totalCredit;

          // Initialize revenue arrays for this date if needed
          if (!farmRevenues[index]) farmRevenues[index] = 0;
          if (!farmCredits[index]) farmCredits[index] = 0;
          if (!shippingRevenues[index]) shippingRevenues[index] = 0;
          if (!returnablePackagingRevenues[index]) returnablePackagingRevenues[index] = 0;
          if (!additionalSalesRevenues[index]) additionalSalesRevenues[index] = 0;

          // Add shipping cost + delivery time charge
          const orderShippingCost = parseFloat(order.shippingCost) || 0;
          const orderDeliveryTimeCharge = parseFloat(order.deliveryTimeCharge) || 0;

          const totalShippingRevenue = orderShippingCost + orderDeliveryTimeCharge;
          shippingRevenues[index] += totalShippingRevenue;
          totalShippingCost += totalShippingRevenue;

          // Add to total revenue (gross - don't subtract credits here)
          totalRevenue += order.totalAmount;
          totalOrders++;

          // Calculate discount amount
          let discountAmount = 0;
          if (order.discountCodeAmount && order.discountCodeAmount > 0) {
            discountAmount = parseFloat(order.discountCodeAmount);
          } else if (order.discountCodePercentage && order.discountCodePercentage > 0) {
            // Calculate subtotal from products to apply percentage
            const orderSubtotal = order.userShoppingCart.reduce((acc, item) => {
              const price = typeof item.productPrice === "string" ? parseFloat(item.productPrice) : item.productPrice;
              return acc + price * item.itemCount;
            }, 0);
            discountAmount = orderSubtotal * (order.discountCodePercentage / 100);
          }
          totalDiscounts += discountAmount;

          // Track customer revenues (gross - credits tracked separately)
          const customerIndex = _.findIndex(customerRevenues, (item) => item.id === order.farmorderCustomerId);
          if (customerIndex !== -1) {
            customerRevenues[customerIndex].totalRevenue += order.totalAmount;
            customerRevenues[customerIndex].orderCount += 1;
          } else {
            customerRevenues.push({
              id: order.farmorderCustomerId,
              name: order.farmorderCustomerName,
              totalRevenue: order.totalAmount,
              orderCount: 1,
            });
          }

          // Expand shopping cart to handle bundles
          const expandedCart = expandCartBundles(order.userShoppingCart, allProducts);

          // Loop over all expanded products to categorize revenue
          _.map(expandedCart, (cartProduct) => {
            const quantity = cartProduct.itemCount;
            const paymentPrice =
              typeof cartProduct.productPrice === "string"
                ? parseFloat(cartProduct.productPrice)
                : cartProduct.productPrice;
            const totalRevenue = paymentPrice * quantity;

            // Check if this product was credited
            const creditedProduct = orderCredits.creditedProducts.find((cp) => cp.productId === cartProduct.productId);
            const creditedQuantity = creditedProduct ? creditedProduct.quantity : 0;
            const creditedRevenue = creditedQuantity * paymentPrice;

            // Add to total units sold (net of credits)
            totalUnitsSold += quantity - creditedQuantity;

            // Get product details to find productGroup and weight
            const productDetails = allProducts.find((p) => p.id === cartProduct.productId);
            const productGroup = productDetails?.productGroup || "other";
            const productWeight = productDetails?.weight || 0;

            // Track weight for products with weight > 0
            if (productWeight > 0) {
              const netQuantity = quantity - creditedQuantity;
              totalWeight += productWeight * netQuantity;
              totalRevenueWithWeight += totalRevenue - creditedRevenue;
            }

            // Categorize revenue based on productGroup
            if (FARM_PRODUCT_GROUPS.includes(productGroup)) {
              farmRevenues[index] += totalRevenue;
              totalFarmRevenue += totalRevenue;

              // Track credits for farm products separately
              if (creditedRevenue > 0) {
                farmCredits[index] += creditedRevenue;
                totalFarmCredits += creditedRevenue;
              }
            } else if (productGroup === RETURNABLE_PACKAGING_PRODUCT_GROUP) {
              returnablePackagingRevenues[index] += totalRevenue;
              totalReturnablePackaging += totalRevenue;
              // Subtract credits from returnable packaging
              if (creditedRevenue > 0) {
                returnablePackagingRevenues[index] -= creditedRevenue;
                totalReturnablePackaging -= creditedRevenue;
              }
            } else {
              // packaging, other, or any unknown group
              additionalSalesRevenues[index] += totalRevenue;
              totalAdditionalSales += totalRevenue;
              // Subtract credits from additional sales
              if (creditedRevenue > 0) {
                additionalSalesRevenues[index] -= creditedRevenue;
                totalAdditionalSales -= creditedRevenue;
              }
            }

            // Track product sales (existing logic with credits)
            const productIndex = _.findIndex(boughtProducts, (item) => item.id === cartProduct.productId);
            if (productIndex !== -1) {
              boughtProducts[productIndex].timesBought += quantity;
              boughtProducts[productIndex].timesCredited =
                (boughtProducts[productIndex].timesCredited || 0) + creditedQuantity;
              boughtProducts[productIndex].totalRevenue += totalRevenue - creditedRevenue;
            } else {
              boughtProducts.push({
                id: cartProduct.productId,
                timesBought: quantity,
                timesCredited: creditedQuantity,
                totalRevenue: totalRevenue - creditedRevenue,
                img: cartProduct.productImageDownloadURL,
                name: cartProduct.productName,
                category: cartProduct.productCategory,
              });
            }
          });
        }
        // Calculations for previous period
        if (!start && !end && datesPrevPeriod.includes(deliveryDate)) {
          totalRevenuePrevPeriod += order.totalAmount;
          totalOrdersPrevPeriod++;
        }
        if (today.format(DATE_FORMAT) === deliveryDate) {
          totalRevenueToday += order.totalAmount;
        }
      }
    });

    const bestOrders = _.orderBy(ordersInPeriod, [(o) => o.totalAmount], ["desc"]).slice(0, 5);
    const boughtProductsSorted = _.orderBy(boughtProducts, [(o) => o.totalRevenue], ["desc"]);
    const topCustomers = _.orderBy(customerRevenues, [(o) => o.totalRevenue], ["desc"]).slice(0, 5);

    // Make sure all indexes of revenue arrays have data
    formattedDates.forEach((item, index) => {
      if (!farmRevenues[index]) farmRevenues[index] = 0;
      if (!farmCredits[index]) farmCredits[index] = 0;
      if (!shippingRevenues[index]) shippingRevenues[index] = 0;
      if (!returnablePackagingRevenues[index]) returnablePackagingRevenues[index] = 0;
      if (!additionalSalesRevenues[index]) additionalSalesRevenues[index] = 0;
    });

    const revenue = {
      dataPoints: [
        {
          name: i18n.t("farm_products"),
          data: farmRevenues,
        },
        {
          name: i18n.t("credits"),
          data: farmCredits.map((val) => -val), // Negative values for stacked bar
        },
        {
          name: i18n.t("shipping_costs"),
          data: shippingRevenues,
        },
        {
          name: i18n.t("returnable_packaging"),
          data: returnablePackagingRevenues,
        },
        {
          name: i18n.t("additional_sales"),
          data: additionalSalesRevenues,
        },
      ],
      labels: formattedDates,
      total: totalRevenue,
      totalFarm: totalFarmRevenue,
      totalFarmCredits: totalFarmCredits,
      totalShipping: totalShippingCost,
      totalReturnablePackaging: totalReturnablePackaging,
      totalAdditionalSales: totalAdditionalSales,
      totalDiscounts: totalDiscounts,
      totalNetto: totalFarmRevenue - totalFarmCredits, // Netto = farm products minus credits
      totalToday: totalRevenueToday,
      totalPrevPeriod: totalRevenuePrevPeriod,
      readableDateRange: readableDateRange,
      customDates: !!start && !!end,
      totalWeight: totalWeight,
      averagePricePerKg: totalWeight > 0 ? totalRevenueWithWeight / (totalWeight / 1000) : 0,
    };

    const facts = {
      totalOrders: totalOrders,
      totalOrdersPrevPeriod: totalOrdersPrevPeriod,
      totalRevenue: totalRevenue,
      totalRevenuePrevPeriod: totalRevenuePrevPeriod,
      bestOrder: bestOrders.length > 0 ? bestOrders[0] : null,
      averagePricePerKg: totalWeight > 0 ? totalRevenueWithWeight / (totalWeight / 1000) : 0,
      totalUnitsSold: totalUnitsSold,
    };

    return {
      revenue: revenue,
      facts: facts,
      productSales: { sales: boughtProductsSorted, readableDateRange: readableDateRange },
      bestOrders: { bestOrders: bestOrders, readableDateRange: readableDateRange },
      topCustomers: { customers: topCustomers, readableDateRange: readableDateRange },
      topSellingProducts: { products: boughtProductsSorted.slice(0, 5), readableDateRange: readableDateRange },
    };
  }
};

function getRange(startDate, endDate, type) {
  let fromDate = dayjs(startDate, DATE_FORMAT);
  let toDate = dayjs(endDate, DATE_FORMAT);
  let diff = toDate.diff(fromDate, type);
  let range = [];
  for (let i = 0; i <= diff; i++) {
    range.push(dayjs(startDate, DATE_FORMAT).add(i, type).format(DATE_FORMAT));
  }
  return range;
}
